/**
 * SuperAdmin-only platform-admin API wrappers.
 *
 * All endpoints require `is_superuser=true` on the BE side; calling
 * them as a regular user returns 403. The FE gates the /admin route
 * + sidebar item on the same flag for UX, but the wire is the source
 * of truth — don't rely on UI gating for security.
 */

import { apiClient } from "./apiClient";


export interface PlatformTenant {
  id: string;
  code: string;
  name: string;
  db_name: string;
  is_active: boolean;
  created_at: string | null;
  updated_at: string | null;
  member_count: number;
  admin_count: number;
}

export interface CreateTenantRequest {
  name: string;
  code: string;
  admin_username: string;
  admin_email: string;
  admin_password: string;
  /** Defaults to `tenant_<code>` server-side. */
  db_name?: string;
  send_welcome?: boolean;
}

export interface CreateTenantResponse {
  tenant: PlatformTenant;
  admin_user: {
    id: string;
    username: string;
    email: string;
    is_tenant_admin: boolean;
  };
  /**
   * Steps that did not complete — role assignment, starter data, welcome
   * email. The tenant still exists and works; these are reported rather
   * than failing the whole provision.
   */
  warnings?: string[];
}

export interface PlatformTenantMember {
  user_id: string;
  username: string;
  email: string;
  full_name: string;
  is_admin: boolean;
  is_active: boolean;
  email_verified: boolean;
  last_login: string | null;
}

export interface PlatformTenantDetail extends PlatformTenant {
  members: PlatformTenantMember[];
}

export interface PlatformUserTenantLink {
  code: string;
  name: string;
  is_admin: boolean;
  is_active: boolean;
  joined_at: string | null;
}

export interface PlatformUser {
  id: string;
  username: string;
  email: string;
  full_name: string;
  is_active: boolean;
  is_staff: boolean;
  is_superuser: boolean;
  email_verified: boolean;
  email_verified_at: string | null;
  date_joined: string | null;
  last_login: string | null;
  tenants: PlatformUserTenantLink[];
  tenant_count: number;
}

export interface TenantStudyEntry {
  study_key: string;
  name: string;
  category: string;
  icon: string | null;
  description: string;
  enabled: boolean;
  overridden: boolean;
}

export interface AddMemberRequest {
  user_id?: string;
  username?: string;
  email?: string;
  is_admin?: boolean;
}

export interface PlatformAuditEntry {
  id: string;
  actor_username: string;
  actor_id: string | null;
  action: string;
  target_type: string;
  target_id: string;
  target_label: string;
  payload: Record<string, unknown>;
  ip_address: string | null;
  created_at: string | null;
}

export interface ImpersonationResponse {
  user: PlatformUser;
  tokens: {
    access: string;
    refresh: string;
    tenants: string[];
    default_tenant: string | null;
  };
}

export interface SetPasswordResponse {
  detail: string;
  /** Present only when the server generated the password. */
  password: string | null;
  /** False in this deployment — there is no token blacklist, so tokens
   *  already issued to the user stay valid until they expire. */
  existing_sessions_revoked: boolean;
  user: PlatformUser;
}

export type BulkUserAction = "deactivate" | "activate" | "verify" | "unverify";
export type BulkTenantAction = "deactivate" | "activate";

export interface PlatformStats {
  tenants: {
    total: number;
    active: number;
    inactive: number;
    new_7d: number;
    new_30d: number;
  };
  users: {
    total: number;
    active: number;
    inactive: number;
    verified: number;
    unverified: number;
    superusers: number;
    new_7d: number;
    new_30d: number;
    active_last_7d: number;
  };
  recent_tenants: PlatformTenant[];
  recent_users: PlatformUser[];
}

const ROOT = "/platform-admin";

export const platformAdminApi = {
  getStats: () =>
    apiClient.get<PlatformStats>(`${ROOT}/stats/`, { skipTenant: true }),

  listTenants: (params?: { q?: string; active?: "true" | "false" }) => {
    const qp = new URLSearchParams();
    if (params?.q) qp.set("q", params.q);
    if (params?.active) qp.set("active", params.active);
    const qs = qp.toString();
    return apiClient.get<PlatformTenant[]>(
      `${ROOT}/tenants/${qs ? `?${qs}` : ""}`,
      { skipTenant: true },
    );
  },

  /**
   * Create a tenant and its first administrator.
   *
   * Provisioning is synchronous on the backend — it creates a database and
   * runs the tenant migrations — so this call takes a few seconds rather
   * than returning immediately. Callers should show progress, not assume a
   * fast round trip.
   */
  createTenant: (body: CreateTenantRequest) =>
    apiClient.post<CreateTenantResponse>(`${ROOT}/tenants/`, body, { skipTenant: true }),

  getTenant: (code: string) =>
    apiClient.get<PlatformTenantDetail>(`${ROOT}/tenants/${code}/`, { skipTenant: true }),

  patchTenant: (code: string, patch: Partial<{ is_active: boolean; name: string }>) =>
    apiClient.patch<PlatformTenantDetail>(`${ROOT}/tenants/${code}/`, patch, { skipTenant: true }),

  listUsers: (params?: { q?: string; active?: "true" | "false"; verified?: "true" | "false" }) => {
    const qp = new URLSearchParams();
    if (params?.q) qp.set("q", params.q);
    if (params?.active) qp.set("active", params.active);
    if (params?.verified) qp.set("verified", params.verified);
    const qs = qp.toString();
    return apiClient.get<PlatformUser[]>(
      `${ROOT}/users/${qs ? `?${qs}` : ""}`,
      { skipTenant: true },
    );
  },

  getUser: (id: string) =>
    apiClient.get<PlatformUser>(`${ROOT}/users/${id}/`, { skipTenant: true }),

  patchUser: (
    id: string,
    patch: Partial<{ is_active: boolean; is_superuser: boolean; is_staff: boolean; email_verified: boolean }>,
  ) =>
    apiClient.patch<PlatformUser>(`${ROOT}/users/${id}/`, patch, { skipTenant: true }),

  // Tenant membership
  addTenantMember: (code: string, body: AddMemberRequest) =>
    apiClient.post<PlatformTenantMember>(`${ROOT}/tenants/${code}/members/`, body, { skipTenant: true }),

  patchTenantMember: (code: string, userId: string, patch: { is_admin?: boolean; is_active?: boolean }) =>
    apiClient.patch<PlatformTenantMember>(`${ROOT}/tenants/${code}/members/${userId}/`, patch, { skipTenant: true }),

  removeTenantMember: (code: string, userId: string) =>
    apiClient.delete<{ removed: boolean }>(`${ROOT}/tenants/${code}/members/${userId}/`, { skipTenant: true }),

  // Tenant studies
  listTenantStudies: (code: string) =>
    apiClient.get<TenantStudyEntry[]>(`${ROOT}/tenants/${code}/studies/`, { skipTenant: true }),

  patchTenantStudy: (code: string, studyKey: string, enabled: boolean) =>
    apiClient.patch<TenantStudyEntry>(`${ROOT}/tenants/${code}/studies/${studyKey}/`, { enabled }, { skipTenant: true }),

  deleteTenant: (code: string, confirm: string) =>
    apiClient.delete<{ deleted: boolean; code: string; db_name: string }>(
      `${ROOT}/tenants/${code}/?confirm=${encodeURIComponent(confirm)}`,
      { skipTenant: true },
    ),

  impersonate: (userId: string) =>
    apiClient.post<ImpersonationResponse>(`${ROOT}/users/${userId}/impersonate/`, {}, { skipTenant: true }),

  /**
   * Set any user's password, in any tenant.
   *
   * Pass `generate` to have the server mint a strong one and return it
   * once — it comes back only in that case, since a password the admin
   * typed is already in their hands and echoing it just puts it through
   * another response body.
   */
  setPassword: (userId: string, body: { password?: string; generate?: boolean }) =>
    apiClient.post<SetPasswordResponse>(
      `${ROOT}/users/${userId}/set-password/`, body, { skipTenant: true }),

  bulkUsers: (ids: string[], action: BulkUserAction) =>
    apiClient.post<{ action: string; updated: number; skipped: number }>(
      `${ROOT}/users/bulk/`,
      { ids, action },
      { skipTenant: true },
    ),

  bulkTenants: (codes: string[], action: BulkTenantAction) =>
    apiClient.post<{ action: string; updated: number }>(
      `${ROOT}/tenants/bulk/`,
      { codes, action },
      { skipTenant: true },
    ),

  listAudit: (params?: { action?: string; target_type?: string; limit?: number }) => {
    const qp = new URLSearchParams();
    if (params?.action) qp.set("action", params.action);
    if (params?.target_type) qp.set("target_type", params.target_type);
    if (params?.limit) qp.set("limit", String(params.limit));
    const qs = qp.toString();
    return apiClient.get<PlatformAuditEntry[]>(
      `${ROOT}/audit/${qs ? `?${qs}` : ""}`,
      { skipTenant: true },
    );
  },

  // CSV downloads. The apiClient JSON path doesn't suit CSV blob
  // responses, so we construct a fetch directly. The browser will
  // download via the Content-Disposition header.
  downloadTenantsCsv: async () => {
    const token = localStorage.getItem("auth_token");
    const base = (import.meta as any).env.VITE_API_URL || "/api";
    const resp = await fetch(`${base}${ROOT}/tenants/export.csv`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      credentials: "include",
    });
    if (!resp.ok) throw new Error(`CSV export failed (${resp.status})`);
    const blob = await resp.blob();
    triggerDownload(blob, "tenants.csv");
  },

  downloadUsersCsv: async () => {
    const token = localStorage.getItem("auth_token");
    const base = (import.meta as any).env.VITE_API_URL || "/api";
    const resp = await fetch(`${base}${ROOT}/users/export.csv`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      credentials: "include",
    });
    if (!resp.ok) throw new Error(`CSV export failed (${resp.status})`);
    const blob = await resp.blob();
    triggerDownload(blob, "users.csv");
  },
};


// ── Impersonation token bookkeeping ─────────────────────────────────────
//
// When a superuser impersonates someone we stash their *real* token
// here so the "return to my account" button can restore it without
// re-authenticating. Anything that reads localStorage.auth_token
// after impersonation will see the impersonated user's token; that
// includes the apiClient, the Layout's user-info fetch, and every
// react-query keyed on the user. Bouncing the page after entering /
// leaving impersonation is the simplest way to get every cache to
// re-fetch under the new identity.

const ORIGINAL_TOKEN_KEY = "auth_token_original";
const ORIGINAL_USER_KEY = "user_original";
const ORIGINAL_TENANT_KEY = "dev_tenant_code_original";
const IMP_META_KEY = "impersonation_meta";
// Mirrors tenantService's storage key. Impersonation has to move the tenant
// in lockstep with the token: every request sends `X-Tenant-Code` from here,
// so swapping only the identity left the session reading a different
// tenant's database than the user it claimed to be acting as.
const TENANT_KEY = "dev_tenant_code";

export interface ImpersonationMeta {
  acting_user_id: string;
  acting_username: string;
  started_at: string;
}

export const impersonationStore = {
  isActive: () => !!localStorage.getItem(ORIGINAL_TOKEN_KEY),

  start: (response: ImpersonationResponse) => {
    const original = localStorage.getItem("auth_token");
    const originalUser = localStorage.getItem("user");
    if (original) localStorage.setItem(ORIGINAL_TOKEN_KEY, original);
    if (originalUser) localStorage.setItem(ORIGINAL_USER_KEY, originalUser);

    localStorage.setItem("auth_token", response.tokens.access);
    localStorage.setItem("user", JSON.stringify(response.user));

    // The token alone does not decide which tenant database is read —
    // `X-Tenant-Code` does, and it was left pointing at the impersonator's
    // own tenant. The impersonated user's studies, files and Foundry
    // objects were simply not in the tenant being queried, so the app
    // showed their name and an empty account. The backend already sends
    // `default_tenant` for this purpose; use it.
    //
    // No tenant means clear the header rather than inherit the
    // impersonator's: a missing tenant fails loudly, the wrong tenant
    // silently shows someone else's data.
    const previousTenant = localStorage.getItem(TENANT_KEY);
    if (previousTenant) localStorage.setItem(ORIGINAL_TENANT_KEY, previousTenant);
    if (response.tokens.default_tenant) {
      localStorage.setItem(TENANT_KEY, response.tokens.default_tenant);
    } else {
      localStorage.removeItem(TENANT_KEY);
    }
    const meta: ImpersonationMeta = {
      acting_user_id: response.user.id,
      acting_username: response.user.username,
      started_at: new Date().toISOString(),
    };
    localStorage.setItem(IMP_META_KEY, JSON.stringify(meta));
  },

  stop: () => {
    const original = localStorage.getItem(ORIGINAL_TOKEN_KEY);
    const originalUser = localStorage.getItem(ORIGINAL_USER_KEY);
    if (original) localStorage.setItem("auth_token", original);
    else localStorage.removeItem("auth_token");
    if (originalUser) localStorage.setItem("user", originalUser);
    else localStorage.removeItem("user");

    const originalTenant = localStorage.getItem(ORIGINAL_TENANT_KEY);
    if (originalTenant) localStorage.setItem(TENANT_KEY, originalTenant);
    else localStorage.removeItem(TENANT_KEY);

    localStorage.removeItem(ORIGINAL_TOKEN_KEY);
    localStorage.removeItem(ORIGINAL_USER_KEY);
    localStorage.removeItem(ORIGINAL_TENANT_KEY);
    localStorage.removeItem(IMP_META_KEY);
  },

  meta: (): ImpersonationMeta | null => {
    const raw = localStorage.getItem(IMP_META_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as ImpersonationMeta;
    } catch {
      return null;
    }
  },
};


function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
